import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import accountsApi from "../../services/accountsCommonService";
import AccountsConstants from "../../common/AccountsConstants";
import { formatINR, ledgerIdOf } from "./accountUser";
import { ApiAlert, FieldHint, FormDialog } from "./AccountWidgets";
import PageShell from "./PageShell";
import "./accounts.css";

const empty = {
  ledgerChoice: "new",
  name: "",
  code: "",
  salesPerson: "",
  contactPerson: "",
  phone: "",
  address: "",
  gstin: "",
  creditLimit: "0",
  creditDays: "0",
  openingBalance: "0",
  isActive: true,
};

export default function CustomersPage() {
  const navigate = useNavigate();
  const [rows, setRows] = useState([]);
  const [search, setSearch] = useState("");
  const [unlinked, setUnlinked] = useState([]);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);
  const [dialog, setDialog] = useState(null);

  const load = async (nextSearch = search) => {
    setLoading(true);
    setError(null);
    try {
      const [customers, ledgers] = await Promise.all([
        accountsApi.customers({ search: nextSearch.trim() }),
        accountsApi.ledgers({ group: "SUNDRY_DEBTOR", isActive: "true" }),
      ]);
      const list = customers?.data || [];
      const used = new Set(list.map((row) => String(row.ledgerId)));
      setRows(list);
      setUnlinked((ledgers?.data || []).filter((row) => !used.has(ledgerIdOf(row))));
    } catch (err) {
      setError(err);
    } finally {
      setLoading(false);
    }
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(""); }, []);

  return (
    <PageShell title="Customers" subtitle="Credit customers for the sales cycle">
      <ApiAlert error={error} />
      <form className="accounts-form" onSubmit={(event) => { event.preventDefault(); load(search); }}>
        <div className="accounts-filter-bar">
          <label className="tally-field">Search by name
            <input className="form-control" value={search} placeholder="Type a customer name" onChange={(event) => setSearch(event.target.value)} />
          </label>
          <div className="accounts-toolbar">
            <button className="btn btn-outline-secondary" type="submit">Search</button>
            <button className="btn btn-primary" type="button" onClick={() => setDialog({ ...empty })}>New customer</button>
          </div>
        </div>
      </form>
      <div className={`accounts-table-wrap ${loading ? "accounts-dim" : ""}`}>
        <table className="accounts-table">
          <thead>
            <tr>
              <th>Customer</th><th>Sales person</th><th className="num">Credit limit</th><th className="num">Credit days</th>
              <th className="num">Outstanding</th><th className="num">Amount due</th><th className="num">Available credit</th><th>Status</th><th />
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => {
              const credit = row.creditStatus || {};
              return (
                <tr key={row.id || row._id}>
                  <td>
                    <button type="button" className="accounts-voucher-link" onClick={() => navigate(AccountsConstants.customerOutstanding(row.id || row._id))}>
                      {row.name}
                    </button>
                    <div className="accounts-hint">{[row.code, row.phone].filter(Boolean).join(" · ")}</div>
                  </td>
                  <td>{row.salesPerson}</td>
                  <td className="num">{formatINR(credit.creditLimit ?? row.creditLimit)}</td>
                  <td className="num">{row.creditDays}</td>
                  <td className="num">{formatINR(credit.outstanding)}</td>
                  <td className={`num ${Number(credit.amountDue) > 0 ? "accounts-money-neg" : ""}`}>{formatINR(credit.amountDue)}</td>
                  <td className={`num ${Number(credit.availableCredit) < 0 ? "accounts-money-neg" : ""}`}>{formatINR(credit.availableCredit)}</td>
                  <td><span className={`accounts-chip ${row.isActive ? "ok" : ""}`}>{row.isActive ? "Active" : "Inactive"}</span></td>
                  <td><button type="button" className="btn btn-link" onClick={() => setDialog({ ...row, creditLimit: String(row.creditLimit ?? 0), creditDays: String(row.creditDays ?? 0) })}>Edit</button></td>
                </tr>
              );
            })}
            {rows.length === 0 && <tr><td colSpan={9}>No customers yet. Add one to start taking orders.</td></tr>}
          </tbody>
        </table>
      </div>
      {dialog && <CustomerDialog form={dialog} unlinked={unlinked} onClose={() => setDialog(null)} onSaved={() => { setDialog(null); load(); }} />}
    </PageShell>
  );
}

function CustomerDialog({ form: initial, unlinked, onClose, onSaved }) {
  const editing = Boolean(initial.id || initial._id);
  const [form, setForm] = useState(initial);
  const [error, setError] = useState(null);
  const [saving, setSaving] = useState(false);
  const set = (key) => (event) => setForm((current) => ({ ...current, [key]: event.target.type === "checkbox" ? event.target.checked : event.target.value }));
  const field = (name) => error?.fieldErrors?.[name];

  const pickLedger = (event) => {
    const value = event.target.value;
    const ledger = unlinked.find((row) => ledgerIdOf(row) === value);
    setForm((current) => ({ ...current, ledgerChoice: value || "new", name: ledger ? ledger.name : current.name }));
  };

  const save = async (event) => {
    event.preventDefault();
    setSaving(true);
    setError(null);
    const body = {
      name: form.name.trim(),
      code: form.code || "",
      salesPerson: form.salesPerson || "",
      contactPerson: form.contactPerson || "",
      phone: form.phone || "",
      address: form.address || "",
      gstin: form.gstin || "",
      creditLimit: String(form.creditLimit ?? "0").trim(),
      creditDays: Number(form.creditDays || 0),
      isActive: form.isActive !== false,
    };
    if (!editing && form.ledgerChoice && form.ledgerChoice !== "new") body.ledgerId = form.ledgerChoice;
    else if (!editing) body.openingBalance = String(form.openingBalance ?? "0").trim();
    try {
      await accountsApi.saveCustomer(body, editing ? (initial.id || initial._id) : undefined);
      onSaved();
    } catch (err) {
      setError(err);
      setSaving(false);
    }
  };

  return (
    <FormDialog
      title={editing ? "Edit customer" : "New customer"}
      onClose={onClose}
      onSubmit={save}
      wide
      footer={(
        <>
          <button type="button" className="btn btn-outline-secondary" onClick={onClose}>Close</button>
          <button type="submit" className="btn btn-primary" disabled={saving}>{saving ? "Saving…" : "Save customer"}</button>
        </>
      )}
    >
      <ApiAlert error={error} />
      <div className="accounts-form-grid">
        {!editing && unlinked.length > 0 && (
          <label className="tally-field span-2">Account ledger
            <select className="form-select" value={form.ledgerChoice || "new"} onChange={pickLedger}>
              <option value="new">Create a new ledger</option>
              {unlinked.map((row) => <option key={ledgerIdOf(row)} value={ledgerIdOf(row)}>Use existing: {row.name}</option>)}
            </select>
            <FieldHint />
          </label>
        )}
        <label className="tally-field span-2">Customer name *
          <input className={`form-control ${field("name") ? "is-invalid" : ""}`} value={form.name || ""} placeholder="Customer name" onChange={set("name")} autoFocus />
          <FieldHint error={field("name")} />
        </label>
        <label className="tally-field">Code
          <input className="form-control" value={form.code || ""} placeholder="Short code" onChange={set("code")} />
          <FieldHint />
        </label>
        <label className="tally-field">Sales person
          <input className="form-control" value={form.salesPerson || ""} placeholder="Sales person name" onChange={set("salesPerson")} />
          <FieldHint />
        </label>
        <label className="tally-field">Contact person
          <input className="form-control" value={form.contactPerson || ""} placeholder="Contact person" onChange={set("contactPerson")} />
          <FieldHint />
        </label>
        <label className="tally-field">Phone
          <input className="form-control" value={form.phone || ""} placeholder="Phone number" onChange={set("phone")} />
          <FieldHint />
        </label>
        <label className="tally-field span-2">Address
          <textarea className="form-control" rows={2} value={form.address || ""} placeholder="Street, city" onChange={set("address")} />
          <FieldHint />
        </label>
        <label className="tally-field">GSTIN
          <input className="form-control" value={form.gstin || ""} placeholder="GSTIN" onChange={set("gstin")} />
          <FieldHint />
        </label>
        <label className="tally-field">Credit limit *
          <input className={`form-control ${field("creditLimit") ? "is-invalid" : ""}`} value={form.creditLimit ?? "0"} placeholder="0.00" onChange={set("creditLimit")} />
          <FieldHint error={field("creditLimit")} help="Most the customer may owe at any time. 0 = no credit." />
        </label>
        <label className="tally-field">Credit days *
          <input className={`form-control ${field("creditDays") ? "is-invalid" : ""}`} value={form.creditDays ?? "0"} placeholder="0" onChange={set("creditDays")} />
          <FieldHint error={field("creditDays")} />
        </label>
        {!editing && (!form.ledgerChoice || form.ledgerChoice === "new") && (
          <label className="tally-field">Opening balance owed
            <input className="form-control" value={form.openingBalance ?? "0"} placeholder="0.00" onChange={set("openingBalance")} />
            <FieldHint help="What the customer already owes when you start using this app, e.g. the balance from Tally." />
          </label>
        )}
        <label className="accounts-check span-2">
          <input type="checkbox" checked={form.isActive !== false} onChange={set("isActive")} />
          <span>Active</span>
        </label>
      </div>
    </FormDialog>
  );
}
